import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import DateFilterDropdown from './DateFilterDropdown.js';
import analyticsService from '../../services/AnalyticsService';
import { Users, Eye, Award, ArrowLeft } from 'lucide-react';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import {
  analyticsCardGridClass,
  analyticsTopGridClass,
  useAnalyticsCompanyScope,
  buildAnalyticsParams,
} from './common/index.js';
import { formatCurrencyWhole as formatCurrency } from '../../utils/formatCurrency.js';


const UserPerformanceAnalytics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('current_month');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });
  const {
    currentUser,
    companies,
    selectedCompany,
    handleCompanyChange,
    ready,
  } = useAnalyticsCompanyScope({ filter, customDates });

  useEffect(() => {
    if (ready) fetchData();
  }, [filter, selectedCompany, ready]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = buildAnalyticsParams({
        filter,
        customDates,
        selectedCompany,
        currentUser,
      });
      const response = await analyticsService.getUserPerformance(params);
      setData(response);
    } catch (error) {
      console.error('Error fetching user performance:', error);
    } finally {
      setLoading(false);
    }
  };

  const UserCard = ({ user, rank }) => (
    <div className="bg-white rounded-xl shadow-md p-3 sm:p-4 lg:p-6 hover:shadow-lg transition-shadow min-w-0">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          {rank <= 3 && (
            <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 bg-blue-50 text-blue-600`}>
              <Award className="w-5 h-5" />
            </div>
          )}
          <div>
            <h3 className="font-semibold text-gray-800">{user.name}</h3>
            <p className="text-sm text-gray-600">{user.city}</p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/users/view/${user.user_id}`)}
          className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
        >
          <Eye className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs text-gray-600">{t('pages.shipment.title')}</p>
          <p className="text-lg font-bold text-gray-800">{user.performance.shipment_count}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Revenue</p>
          <p className="text-lg font-bold text-green-600">{formatCurrency(user.performance.total_revenue)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Collected</p>
          <p className="text-lg font-bold text-emerald-600">{formatCurrency(user.performance.collected)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Collection Rate</p>
          <p className="text-lg font-bold text-blue-600">{user.performance.collection_rate}%</p>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="border-t border-gray-200 pt-3">
        <p className="text-xs text-gray-600 mb-2">Shipment Status</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(user.shipment_status_breakdown).map(([status, count]) => (
            <span key={status} className="px-2 py-1 bg-gray-100 text-xs rounded-full">
              {status}: {count}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout fullWidth>
        <Loader fullScreen={false} size="default" message="Loading User Performance..." />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout fullWidth>
              <div className="w-full">
                {/* Header with Date Filter */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-semibold text-secondary">User Performance Analytics</h1>
                    <p className="text-sm text-gray-600">Agent and employee metrics</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <DateFilterDropdown
                      filter={filter}
                      setFilter={setFilter}
                      customDates={customDates}
                      setCustomDates={setCustomDates}
                      onApply={fetchData}
                      dateRange={data?.filter}
                      currentUser={currentUser}
                      companies={companies}
                      selectedCompany={selectedCompany}
                      onCompanyChange={handleCompanyChange}
                      showCompanyFilter
                    />
                    <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/analytics')}>Back</Button>
                  </div>
                </div>

                {/* Top Performers */}
                {data?.top_performers && data.top_performers.length > 0 && (
                  <div className="mb-6">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <Award className="w-6 h-6 text-yellow-500 mr-2" />
                      Top Performers
                    </h2>
                    <div className={analyticsTopGridClass}>
                      {data.top_performers.slice(0, 3).map((user, index) => (
                        <UserCard key={user.user_id} user={user} rank={index + 1} />
                      ))}
                    </div>
                  </div>
                )}

                {/* All Users */}
                {data?.users && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">All Users Performance</h2>
                    <div className={analyticsCardGridClass}>
                      {data.users.map((user, index) => (
                        <UserCard key={user.user_id} user={user} rank={index + 1} />
                      ))}
                    </div>
                  </div>
                )}

                {data?.users && data.users.length === 0 && (
                  <div className="text-center py-12 bg-gray-50 rounded-xl">
                    <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No user performance data available for the selected period</p>
                  </div>
                )}
              </div>
    </DashboardLayout>
  );
};

export default UserPerformanceAnalytics;